import {
  CustomerOrderBase,
  ReturnLabelRecord,
  ShipmentLineItemBase,
} from '@lambda/commons/smartforms/types';
import { EsTracking } from '@lambda/commons/apis/elasticsearch/types';
import { AfterShipT } from '@lambda/apis/src/aftership/types';

export const backendBaseUrl =
  process.env.NEXT_PUBLIC_STAGE === 'prod' || process.env.STAGE === 'prod'
    ? 'https://a.reebelo.com/ops'
    : 'https://a.reebelo.blue/ops';

export type UploadFile = {
  filename: string;
  type: string;
  bucketUrl: string;
  status: 'uploading' | 'completed' | 'error';
};

export type UploadS3Url = {
  signedUrl: string;
  bucketUrl: string;
};

export type FileName = {
  filename: string;
  type: string;
};

export type Props = {
  order: {
    orderNumber: string;
    lineItemId: string;
    rcOrderId?: string;
  };
};

export type MediaUploadZoneProps = {
  type: 'image' | 'multimedia' | 'pdf';
  descriptionType: 'minimum' | 'optional' | 'complete';
  files: UploadFile[];
  showCounts?: boolean;
  showTitle?: boolean;
  showFooter?: boolean;
  setUploadFile: (files: UploadFile[]) => void;
  callback: (files: File[]) => void;
  shouldMediaUploadBeOptional?: boolean | '';
};

export type MediaUploadZoneOldProps = {
  type: 'image' | 'multimedia';
  descriptionType: 'minimum' | 'optional' | 'complete';
  files: File[];
  showCounts?: boolean;
  showTitle?: boolean;
  showFooter?: boolean;
  callback: (files: File[]) => void;
};

export type ShipmentStatus =
  | 'ordered'
  | 'dispatched'
  | 'outForDelivery'
  | 'delivered';

export type PopupMessage = null | { title: string; description: string };

export interface FulFillmentStatusMap {
  [key: string]: ShipmentStatus;
}

export type HelpFormProp = {
  order: {
    orderNumber: string;
    vendorName: string;
  };
};

export type FulfillmentStatusBarProps = {
  status: 'ordered' | 'dispatched' | 'outForDelivery' | 'delivered';
  isRefunded: boolean;
  orderedDate: string;
  dispatchedDate?: string;
  outForDeliveryDate?: string;
  deliveredDate?: string;
  canceledAt: string | null;
  storeTimeZone: string;
  isHelpPage?: boolean;
};

export type LineItemCGLink = { lineItemId: string; cgLink: string };

export type NonReebeloCare = {
  isCovered: false;
  isWarrantyEligible: boolean;
};
export type ReebeloCareInfo = {
  isCovered: true;
  reebeloCareVendor: string;
  coverGeniusUrl?: string;
  reebeloCareLineItemId: string;
  rcOrderId?: string;
};

export interface ShipmentLineItem extends ShipmentLineItemBase {
  reebeloId?: string;
  quantity: number;
  image: string;
  properties: Array<Record<string, string>>;
  isFulfilled: boolean;
  isRefunded: boolean;
  refundQuantity?: number | null;
  vendor: string;
  isSmartphone: boolean;
  reebeloCare: NonReebeloCare | ReebeloCareInfo;
  imeiNumber: string | null;
  price: number;
  tags: string[];
  sku: string;
  bestOffer?: {
    productId: number;
    variantId: number;
    reebeloPk: string | undefined;
  } | null;
}

export interface Shipment {
  lineItems: Array<ShipmentLineItem>;
  courierTrackingUrl?: string;
  dispatchedBy?: string;
  dispatchedAt?: string;
  shippedBy?: string;
  deliveredBy?: string;
  tracking?:
    | (AfterShipT.Tracking & {
        status?: string | null;
      })
    | (Partial<EsTracking> & {
        tracking_number: string;
        checkpoints: null;
        shipment_delivery_date: null;
        shipment_pickup_date: null;
        tag: null;
      });
  invoiceUrl?: string;
}

export type LineItemsT = Array<{
  title: string;
  vendor?: string;
  imageUrl: string;
  tags?: string[];
  properties?: { name: string; value: string }[];
  category?: string;
  psku?: string;
}>;
export interface CustomerOrder extends Omit<CustomerOrderBase, 'shipments'> {
  id: string;
  fulfillmentStatus: string;
  phone: string | null;
  canceledAt: string | null;
  cancelReason: string | null;
  totalPrice: {
    amount: number;
    currencyCode: string;
  };
  shippingAddress: {
    firstName: string;
    lastName: string;
    address1: string;
    address2?: string;
    city: string;
    province: string;
    country: string;
    zip: string;
  };
  shipments: Array<Shipment>;
  lineItems?: LineItemsT;
  returnLabelRecord?: ReturnLabelRecord;
}
